import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import api from '../utils/api.util';
import toast from '../utils/toast.util';
import { useAuth } from '../auth/AuthContext';
import { ENDPOINT } from '../constants/common-app.const';
import { ClientType } from '../pages/CourseEnrollment/CourseEnrollment.const';

type CourseFormat = 'Очное' | 'Онлайн' | 'Очное / Онлайн';
type AvailableTag = 'Юрлица' | 'ИП' | 'Физлица';

export interface CourseCard {
  id: string;
  title: string;
  description: string;
  format: CourseFormat;
  duration: string;
  pricePerStudent: string;
  priceKopecks: number;
  groupPeriod: string;
  availableFor: AvailableTag[];
  allowedClientTypes: ClientType[];
}

const SUFFIX_FORMAT_MAP: Record<string, string> = {
  online: 'Онлайн',
  offline: 'Очное',
  mixed: 'Очное / Онлайн',
};

const pluralDays = (n: number): string => {
  if (n === 1) return 'день';
  if (n >= 2 && n <= 4) return 'дня';
  return 'дней';
};

const transformCourse = (c: any): CourseCard => ({
  id: c.id,
  title: c.name,
  description: c.description ?? '',
  format: (SUFFIX_FORMAT_MAP[c.suffix] ?? c.suffix) as CourseFormat,
  duration: `${c.durationWorkingDays} рабочих ${pluralDays(c.durationWorkingDays)}`,
  pricePerStudent: `${Math.round(c.priceKopecks / 100)} BYN / слушатель`,
  priceKopecks: c.priceKopecks,
  groupPeriod: c.nearestGroup
    ? `${dayjs(c.nearestGroup.startDate).format('DD.MM.YYYY')} – ${dayjs(c.nearestGroup.endDate).format('DD.MM.YYYY')}`
    : 'Группы не назначены',
  availableFor: [
    c.clientTypeLegal && 'Юрлица',
    c.clientTypeSoleProprietor && 'ИП',
    c.clientTypeIndividual && 'Физлица',
  ].filter(Boolean) as AvailableTag[],
  allowedClientTypes: [
    c.clientTypeLegal && 'legal',
    c.clientTypeSoleProprietor && 'ip',
    c.clientTypeIndividual && 'individual',
  ].filter(Boolean) as ClientType[],
});

const useGetCourses = () => {
  const [courses, setCourses] = useState<CourseCard[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();

  const fetchCourses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(ENDPOINT.COURSES);
      setCourses((response ?? []).map(transformCourse));
      setLoading(false);
    } catch (err) {
      toast.showOnFailure('Ошибка загрузки списка курсов');
      setError('fetch failed');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCourses();
    }
  }, [isAuthenticated, fetchCourses]);

  return { courses, loading, error };
};

export default useGetCourses;
